import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Mail, Phone, MapPin, Calendar, Tag, User, Building, Clock, LayoutGrid } from 'lucide-react';
import { useTranslation } from '../context/TranslationContext';
import { persistActiveContactForConfigurator } from '../lib/contactConfigurator';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import axios from 'axios';

import { API_URL } from '../config/api';


const ContactProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContact();
  }, [id]);

  const fetchContact = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/contacts/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      setContact(response.data);
    } catch (error) {
      console.error('Error fetching contact:', error);
      alert('Failed to load contact details');
      navigate('/dashboard?tab=contacts');
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (firstName, lastName) => {
    const first = firstName ? firstName.charAt(0).toUpperCase() : '';
    const last = lastName ? lastName.charAt(0).toUpperCase() : '';
    return first + last || '?';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading contact details...</p>
        </div>
      </div>
    );
  }

  if (!contact) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Contact not found</p>
          <Button onClick={() => navigate('/dashboard?tab=contacts')} className="mt-4">
            Back to Contacts
          </Button>
        </div>
      </div>
    );
  }

  const ghlData = contact.ghl_data || {};

  const handleOpenConfigurator = () => {
    const selectedContact = persistActiveContactForConfigurator(contact, user?.id);
    navigate('/configurator-select', {
      state: {
        selectedContact,
        fromContactProfile: true,
        returnTo: `/dashboard/contact/${id}`,
        preserveQuoteSession: true,
      },
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={() => navigate('/dashboard?tab=contacts')}
            className="mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Contacts
          </Button>
          
          <div className="flex items-center space-x-4 bg-white p-6 rounded-lg shadow-sm">
            <div className="h-20 w-20 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center shadow-md">
              <span className="text-white font-semibold text-2xl">
                {getInitials(contact.first_name, contact.last_name)}
              </span>
            </div>
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900">
                {contact.first_name} {contact.last_name}
              </h1>
              {contact.notes && (
                <div className="mt-2">
                  <Badge variant="outline" className="text-sm">
                    {contact.notes}
                  </Badge>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <User className="mr-2 h-5 w-5" />
                  Basic Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {contact.email && (
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Email</label>
                      <div className="flex items-center space-x-2">
                        <Mail className="h-4 w-4 text-gray-400" />
                        <p className="text-sm text-gray-900">{contact.email}</p>
                      </div>
                    </div>
                  )}

                  {contact.phone && (
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Phone</label>
                      <div className="flex items-center space-x-2">
                        <Phone className="h-4 w-4 text-gray-400" />
                        <p className="text-sm text-gray-900">{contact.phone}</p>
                      </div>
                    </div>
                  )}

                  {(contact.address || contact.city || contact.postal_code) && (
                    <div className="space-y-1 md:col-span-2">
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Address</label>
                      <div className="flex items-start space-x-2">
                        <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                        <p className="text-sm text-gray-900">
                          {contact.address && contact.address}
                          {contact.city && (contact.address ? `, ${contact.city}` : contact.city)}
                          {contact.postal_code && ` ${contact.postal_code}`}
                          {contact.country && `, ${contact.country}`}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Created</label>
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <p className="text-sm text-gray-900">{formatDate(contact.created_at)}</p>
                    </div>
                  </div>

                  {contact.updated_at && contact.updated_at !== contact.created_at && (
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Last Updated</label>
                      <div className="flex items-center space-x-2">
                        <Clock className="h-4 w-4 text-gray-400" />
                        <p className="text-sm text-gray-900">{formatDate(contact.updated_at)}</p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* GHL Data - Full Details */}
            {ghlData && Object.keys(ghlData).length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Building className="mr-2 h-5 w-5" />
                    Complete GHL Information
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Contact Name Details */}
                    {(ghlData.contactName || ghlData.firstName || ghlData.lastName) && (
                      <div>
                        <h3 className="text-sm font-semibold text-gray-700 mb-2">Name Details</h3>
                        <div className="bg-gray-50 p-3 rounded-md space-y-1">
                          {ghlData.contactName && <p className="text-sm"><span className="font-medium">Contact Name:</span> {ghlData.contactName}</p>}
                          {ghlData.firstName && <p className="text-sm"><span className="font-medium">First Name:</span> {ghlData.firstName}</p>}
                          {ghlData.firstNameRaw && <p className="text-sm"><span className="font-medium">First Name (Raw):</span> {ghlData.firstNameRaw}</p>}
                          {ghlData.lastName && <p className="text-sm"><span className="font-medium">Last Name:</span> {ghlData.lastName}</p>}
                          {ghlData.lastNameRaw && <p className="text-sm"><span className="font-medium">Last Name (Raw):</span> {ghlData.lastNameRaw}</p>}
                        </div>
                      </div>
                    )}

                    {/* Contact Information */}
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-2">Contact Information</h3>
                      <div className="bg-gray-50 p-3 rounded-md space-y-1">
                        {ghlData.email && <p className="text-sm"><span className="font-medium">Email:</span> {ghlData.email}</p>}
                        {ghlData.phone && <p className="text-sm"><span className="font-medium">Phone:</span> {ghlData.phone}</p>}
                        {ghlData.website && <p className="text-sm"><span className="font-medium">Website:</span> {ghlData.website}</p>}
                        {ghlData.companyName && <p className="text-sm"><span className="font-medium">Company:</span> {ghlData.companyName}</p>}
                      </div>
                    </div>

                    {/* Address Details — GHL search uses `address`, list/get often use `address1` */}
                    {(ghlData.address1 || ghlData.address || ghlData.city || ghlData.state || ghlData.postalCode || ghlData.country) && (
                      <div>
                        <h3 className="text-sm font-semibold text-gray-700 mb-2">Address Details</h3>
                        <div className="bg-gray-50 p-3 rounded-md space-y-1">
                          {(ghlData.address1 || ghlData.address) && (
                            <p className="text-sm">
                              <span className="font-medium">Address:</span> {ghlData.address1 || ghlData.address}
                            </p>
                          )}
                          {ghlData.city && <p className="text-sm"><span className="font-medium">City:</span> {ghlData.city}</p>}
                          {ghlData.state && <p className="text-sm"><span className="font-medium">State:</span> {ghlData.state}</p>}
                          {ghlData.postalCode && <p className="text-sm"><span className="font-medium">Postal Code:</span> {ghlData.postalCode}</p>}
                          {ghlData.country && <p className="text-sm"><span className="font-medium">Country:</span> {ghlData.country}</p>}
                        </div>
                      </div>
                    )}

                    {/* Additional Information */}
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-2">Additional Information</h3>
                      <div className="bg-gray-50 p-3 rounded-md space-y-1">
                        {ghlData.type && <p className="text-sm"><span className="font-medium">Type:</span> <Badge variant="outline" className="ml-1">{ghlData.type}</Badge></p>}
                        {ghlData.source && <p className="text-sm"><span className="font-medium">Source:</span> {ghlData.source}</p>}
                        {ghlData.assignedTo && <p className="text-sm"><span className="font-medium">Assigned To:</span> {ghlData.assignedTo}</p>}
                        {ghlData.dnd !== undefined && <p className="text-sm"><span className="font-medium">Do Not Disturb:</span> {ghlData.dnd ? 'Yes' : 'No'}</p>}
                        {ghlData.dateOfBirth && <p className="text-sm"><span className="font-medium">Date of Birth:</span> {formatDate(ghlData.dateOfBirth)}</p>}
                        {ghlData.timezone && <p className="text-sm"><span className="font-medium">Timezone:</span> {ghlData.timezone}</p>}
                      </div>
                    </div>

                    {/* Tags */}
                    {ghlData.tags && Array.isArray(ghlData.tags) && ghlData.tags.length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold text-gray-700 mb-2">Tags</h3>
                        <div className="flex flex-wrap gap-2">
                          {ghlData.tags.map((tag, index) => (
                            <Badge key={index} variant="outline" className="text-xs">
                              <Tag className="mr-1 h-3 w-3" />
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Dates */}
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-2">Important Dates</h3>
                      <div className="bg-gray-50 p-3 rounded-md space-y-1">
                        {ghlData.dateAdded && <p className="text-sm"><span className="font-medium">Date Added:</span> {formatDate(ghlData.dateAdded)}</p>}
                        {ghlData.dateUpdated && <p className="text-sm"><span className="font-medium">Date Updated:</span> {formatDate(ghlData.dateUpdated)}</p>}
                      </div>
                    </div>

                    {/* Additional Emails */}
                    {ghlData.additionalEmails && Array.isArray(ghlData.additionalEmails) && ghlData.additionalEmails.length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold text-gray-700 mb-2">Additional Emails</h3>
                        <div className="bg-gray-50 p-3 rounded-md space-y-1">
                          {ghlData.additionalEmails.map((email, index) => (
                            <p key={index} className="text-sm">{email}</p>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Custom Fields */}
                    {ghlData.customFields && Array.isArray(ghlData.customFields) && ghlData.customFields.length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold text-gray-700 mb-2">Custom Fields</h3>
                        <div className="bg-gray-50 p-3 rounded-md space-y-2">
                          {ghlData.customFields.map((field, index) => (
                            <div key={index} className="text-sm">
                              <span className="font-medium">{field.name || field.field || 'Field'}:</span> {field.value || 'N/A'}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Followers */}
                    {ghlData.followers && Array.isArray(ghlData.followers) && ghlData.followers.length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold text-gray-700 mb-2">Followers</h3>
                        <div className="bg-gray-50 p-3 rounded-md space-y-1">
                          {ghlData.followers.map((follower, index) => (
                            <p key={index} className="text-sm">{follower}</p>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Raw JSON View (Collapsible) */}
                    <details className="mt-4">
                      <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
                        View Raw GHL Data (JSON)
                      </summary>
                      <div className="mt-2 bg-gray-900 text-gray-100 p-4 rounded-md overflow-x-auto">
                        <pre className="text-xs">{JSON.stringify(ghlData, null, 2)}</pre>
                      </div>
                    </details>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {isAuthenticated && user ? (
                  <Button
                    className="w-full justify-start bg-orange-600 hover:bg-orange-700 text-white"
                    onClick={handleOpenConfigurator}
                  >
                    <LayoutGrid className="mr-2 h-4 w-4" />
                    {t('contactProfile.openConfigurator')}
                  </Button>
                ) : null}
                {contact.email && (
                  <Button variant="outline" className="w-full justify-start" onClick={() => window.location.href = `mailto:${contact.email}`}>
                    <Mail className="mr-2 h-4 w-4" />
                    Send Email
                  </Button>
                )}
                {contact.phone && (
                  <Button variant="outline" className="w-full justify-start" onClick={() => window.location.href = `tel:${contact.phone}`}>
                    <Phone className="mr-2 h-4 w-4" />
                    Call Contact
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* GHL ID */}
            {ghlData.id && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">GHL Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">GHL Contact ID</label>
                      <p className="text-sm font-mono text-gray-900 mt-1 break-all">{ghlData.id}</p>
                    </div>
                    {ghlData.locationId && (
                      <div>
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Location ID</label>
                        <p className="text-sm font-mono text-gray-900 mt-1 break-all">{ghlData.locationId}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactProfile;

